/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { hooks as commonHooks } from '@sitebuilder/common';
import AdminMainPanel from '../Layouts/AdminMainPanel';
import NavBar from '../Layouts/NavBar';
import { StateShape as ReduxStateShape } from '../../reducers';
import { actionCreators } from '../../reducers/media';
import { actionCreators as templateEditorActionCreators } from '../../reducers/templateEditor';
import { ItemsTable } from '../BaseComponents';
import { PageActionOption, mediaActionOptions } from '../Layouts/LeftSidebar/NavOptions';
import MediaRightSidebar from './MediaRightSidebar';

const Media = () => {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const [pageAction, setPageAction] = useState('');
    const [urlState, setUrlState] = useState<string>('');
    const { medias, selectedMedia, showDeletedItems } = useSelector(
        (state: ReduxStateShape) => state.media
    );

    // Extract folder path from URL
    const getFolderPathFromUrl = () => {
        const path = location.pathname;
        // Remove '/admin/media' from the path to get the folder path
        const folderPath = path.replace('/admin/media', '');
        return folderPath || '';
    };

    useEffect(() => {
        // Hide main left side bar when on medias list page
        dispatch(templateEditorActionCreators.setEditor(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Get folder path from URL and load media for that folder
        const folderPath = getFolderPathFromUrl();
        // Only update urlState if it's different from current value
        if (urlState !== folderPath) {
            setUrlState(folderPath);
        }
        dispatch(
            actionCreators.listMedias(folderPath, {
                idPattern: [],
                authorsIn: [],
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        // prevent calling api on each and every render
        // call only when `showDeletedItems` is true
        if (showDeletedItems) {
            const folderPath = getFolderPathFromUrl();
            dispatch(
                actionCreators.listMedias(folderPath, {
                    idPattern: [],
                    authorsIn: [],
                })
            );
        }
    }, [dispatch, showDeletedItems, location.pathname]);

    const onClickRow = (mediaName: string, isSelected: boolean) => {
        if (isSelected) {
            dispatch(actionCreators.unselectMedia([mediaName]));
        } else {
            dispatch(actionCreators.setSelectedMedia([mediaName]));
        }
        setPageAction('');
    };

    const onClickNewMedia = () => {
        dispatch(actionCreators.unselectAllMedia());
        setPageAction(pageAction === 'media' ? '' : 'media');
    };

    const onClickNewFolder = () => {
        dispatch(actionCreators.unselectAllMedia());
        setPageAction(pageAction === 'folder' ? '' : 'folder');
    };

    const i18n = commonHooks.useLocalization();
    const showRightSidebar =
        pageAction === 'media' || pageAction === 'folder' || selectedMedia.length > 0;

    return (
        <div className="media">
            <AdminMainPanel>
                <NavBar
                    rightSidebar={
                        <MediaRightSidebar
                            addNewMedia={pageAction === 'media'}
                            urlState={urlState}
                            addNewFolder={pageAction === 'folder'}
                        />
                    }
                    hideRightSidebar={!showRightSidebar}
                />
                <div className="admin-main-content">
                    <ItemsTable
                        tableType={'media'}
                        redirectBaseUrl={'/admin/media'}
                        items={medias}
                        selectedItems={selectedMedia}
                        onClickRow={onClickRow}
                        onUrlStateChange={(newUrlState: string) => {
                            // Only update if the URL state actually changed
                            if (newUrlState !== urlState) {
                                setUrlState(newUrlState);
                                // Update browser URL when folder navigation occurs
                                const newPath = newUrlState
                                    ? `/admin/media${newUrlState}`
                                    : '/admin/media';
                                if (location.pathname !== newPath) {
                                    history.push(newPath);
                                }
                            }
                        }}
                        initialUrlState={getFolderPathFromUrl()}
                        title={i18n.t('media_title')}
                        subtitle={i18n.t('media_subtitle')}
                        showDeletedItems={showDeletedItems}
                        onClickShowDeletedItems={() =>
                            dispatch(actionCreators.setShowDeletedItems())
                        }
                        options={mediaActionOptions}
                        selectedOption={mediaActionOptions.find((o) => o.name === pageAction)}
                        onClickOption={(opt: PageActionOption) => {
                            if (opt.name === 'folder') {
                                onClickNewFolder();
                            } else if (opt.name === 'media') {
                                onClickNewMedia();
                            }
                        }}
                    />
                </div>
            </AdminMainPanel>
        </div>
    );
};

export default Media;
